// src/components/ReadjudicacionMasiva/Documentos/ResumenGeneracion.js

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Paper,
    Typography,
    Box,
    Grid,
    Chip,
    Divider,
    LinearProgress
} from '@mui/material';
import {
    Description as DescriptionIcon,
    Email as EmailIcon,
    Gavel as GavelIcon,
    Receipt as ReceiptIcon,
    Mail as MailIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as HourglassEmptyIcon
} from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3),
        position: 'sticky',
        top: theme.spacing(2)
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(2)
    },
    statBox: {
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        textAlign: 'center',
        marginBottom: theme.spacing(2)
    },
    statValue: {
        fontSize: '2.5rem',
        fontWeight: 700,
        color: theme.palette.primary.main,
        lineHeight: 1
    },
    statLabel: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1)
    },
    documentoItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius
    },
    documentoNombre: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '0.875rem',
        fontWeight: 500
    },
    divider: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    estadoChip: {
        marginTop: theme.spacing(2)
    },
    progressBox: {
        marginTop: theme.spacing(2)
    }
}));

const iconosDocumentos = {
    CARATULA: DescriptionIcon,
    CARTA_PODER: EmailIcon,
    DEMANDA: GavelIcon,
    RECIBO: ReceiptIcon,
    SOBRES: MailIcon
};

const nombresDocumentos = {
    CARATULA: 'Carátula',
    CARTA_PODER: 'Carta Poder',
    DEMANDA: 'Demanda',
    RECIBO: 'Recibo',
    SOBRES: 'Sobres'
};

const ResumenGeneracion = ({ lote, documentosSeleccionados, generando, progreso }) => {
    const classes = useStyles();

    const totalDocumentos = documentosSeleccionados.length;
    // Usar RegistrosValidos que viene de ObtenerLote, o Correctos si viene de ListarLotes
    const totalRegistros = lote?.RegistrosValidos || lote?.Correctos || 0;
    const totalArchivos = totalDocumentos * totalRegistros;

    return (
        <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.title}>
                Resumen de Generación
            </Typography>

            {/* Estadística principal */}
            <Box className={classes.statBox}>
                <Typography className={classes.statValue}>
                    {totalArchivos}
                </Typography>
                <Typography className={classes.statLabel}>
                    Documentos a Generar
                </Typography>
            </Box>

            {/* Detalles */}
            <Grid container spacing={2}>
                <Grid size={6}>
                    <Box textAlign="center">
                        <Typography variant="h6" color="primary">
                            {totalRegistros}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Registros
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={6}>
                    <Box textAlign="center">
                        <Typography variant="h6" color="primary">
                            {totalDocumentos}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Tipos de Doc.
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            <Divider className={classes.divider} />

            {/* Lista de documentos seleccionados */}
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
                Documentos Seleccionados
            </Typography>

            {documentosSeleccionados.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" style={{ padding: 16 }}>
                    No hay documentos seleccionados
                </Typography>
            ) : (
                documentosSeleccionados.map((tipo) => {
                    const IconComponent = iconosDocumentos[tipo];
                    const nombre = nombresDocumentos[tipo];
                    return (
                        <Box key={tipo} className={classes.documentoItem}>
                            <Box className={classes.documentoNombre}>
                                {IconComponent && <IconComponent style={{ fontSize: 20 }} />}
                                {nombre}
                            </Box>
                            <Chip
                                label={totalRegistros}
                                size="small"
                                color="primary"
                            />
                        </Box>
                    );
                })
            )}

            {/* Estado de generación */}
            {generando && (
                <Box className={classes.progressBox}>
                    <Divider className={classes.divider} />
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">
                            Generando...
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {progreso}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progreso}
                        style={{ height: 8, borderRadius: 4 }}
                    />
                </Box>
            )}

            {/* Estado del lote */}
            <Box className={classes.estadoChip} textAlign="center">
                {lote?.Estado === 5 ? (
                    <Chip
                        icon={<CheckCircleIcon />}
                        label="Finalizado"
                        color="primary"
                        size="medium"
                    />
                ) : (
                    <Chip
                        icon={<HourglassEmptyIcon />}
                        label="En Proceso"
                        size="medium"
                    />
                )}
            </Box>
        </Paper>
    );
};

export default ResumenGeneracion;
